import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"

import { useListParams } from "hooks/useListParams"
import {
  useCarCareServices,
  useDeleteCarCareService,
} from "hooks/useCarCareService"
import { formatReadableDate } from "utils/formatDate"

import CarCareServiceAddEdit from "./CarCareServiceAddEdit"
import { CarCareServiceHeader } from "./CarCareServiceHeader"

const TYPE_META = {
  general_service: { label: "General Service", color: "info" },
  accessory: { label: "Accessory", color: "secondary" },
}

const CarCareServiceList = () => {
  document.title = "Car Care Services"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return { page, per_page, search }
  }, [params])

  const {
    data: responseData,
    isLoading,
    refetch,
    isRefetching,
  } = useCarCareServices(queryParams)

  const { mutateAsync: deleteItem, isPending: deleteLoading } =
    useDeleteCarCareService()

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Service deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (err) {
      console.error(err)
      toast.error("Failed to delete service.")
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        size: 60,
        cell: ({ row }) => (
          <div className="text-muted fw-semibold">#{row.original.id}</div>
        ),
      },
      {
        header: "Service",
        accessorKey: "name",
        cell: ({ row }) => (
          <div className="fw-semibold">{row.original.name}</div>
        ),
      },
      {
        header: "Type",
        accessorKey: "type",
        cell: ({ row }) => {
          const meta = TYPE_META[row.original.type] || TYPE_META.general_service
          return (
            <Badge className={`badge-soft-${meta.color}`}>{meta.label}</Badge>
          )
        },
      },
      {
        header: "Parent",
        accessorKey: "parent.name",
        cell: ({ row }) => row.original.parent?.name || "-",
      },
      {
        header: "Default Price",
        accessorKey: "default_price",
        cell: ({ row }) =>
          row.original.default_price != null
            ? Number(row.original.default_price).toLocaleString()
            : "-",
      },
      {
        header: "Status",
        accessorKey: "is_active",
        cell: ({ row }) => (
          <Badge
            className={`badge-soft-${
              row.original.is_active ? "success" : "danger"
            }`}
          >
            {row.original.is_active ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) => (
          <div className="text-muted" style={{ whiteSpace: "nowrap" }}>
            {row.original.created_at
              ? formatReadableDate(row.original.created_at)
              : "-"}
          </div>
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const data = row.original
          return (
            <div className="d-flex gap-1">
              <Button
                color="info"
                size="sm"
                onClick={() => handleEditClick(data)}
                id={`edit-${data.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
              </Button>
              <UncontrolledTooltip target={`edit-${data.id}`}>
                Edit
              </UncontrolledTooltip>

              <Button
                color="danger"
                size="sm"
                onClick={() => handleDeleteClick(data)}
                id={`delete-${data.id}`}
              >
                <i className="mdi mdi-delete-outline" />
              </Button>
              <UncontrolledTooltip target={`delete-${data.id}`}>
                Delete
              </UncontrolledTooltip>
            </div>
          )
        },
      },
    ],
    []
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="Car Care Services" breadcrumbItem="Car Care Services">
      <CarCareServiceHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <DeleteModal
        show={deleteModal}
        loading={deleteLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="my-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        className="table-responsive"
      />

      <CarCareServiceAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={selectedData}
        toggleModal={toggleModal}
      />
    </PageWrapper>
  )
}

export default CarCareServiceList
